// Swiss landing page — monolithic iframe block
// 4 brand variants: bst / nord / klima / orbita

export type SwissLandingBrand = 'bst' | 'nord' | 'klima' | 'orbita'
export interface SwissLandingPageData { brand: SwissLandingBrand }
export const DEFAULT_SWISS_LANDING_DATA: SwissLandingPageData = { brand: 'bst' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const SWISS_LANDING_CSS = `
/* ============ SWISS / INTERNATIONAL TYPOGRAPHIC SYSTEM ============ */
:root{
  --bg:#F7F6F2; --ink:#0E0E0E; --accent:#E4030E; --on-accent:#FFFFFF;
  --line:color-mix(in srgb, var(--ink) 16%, transparent);
  --line-soft:color-mix(in srgb, var(--ink) 8%, transparent);
  --pad:clamp(16px,4vw,56px);
  --mono:"IBM Plex Mono",ui-monospace,SFMono-Regular,monospace;
  --sans:"Archivo",Helvetica,Arial,sans-serif;
}
*{margin:0;padding:0;box-sizing:border-box}
@media (prefers-reduced-motion:no-preference){html{scroll-behavior:smooth}}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--sans); font-size:16px; line-height:1.55;
  transition:background .5s ease,color .5s ease;
  -webkit-font-smoothing:antialiased; overflow-x:hidden;
}
::selection{background:var(--accent);color:var(--on-accent)}
img{display:block;max-width:100%}
a{color:inherit}
.mono{font-family:var(--mono)}
.noise{position:fixed;inset:0;z-index:90;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");}
/* ---------- HEADER ---------- */
.site-head{position:sticky;top:0;z-index:80;display:flex;align-items:center;gap:28px;
  height:64px;padding:0 var(--pad);background:var(--bg);border-bottom:1px solid var(--line);
  transition:background .5s ease}
.brand-id{display:flex;align-items:center;gap:12px;min-width:0}
.emblem svg{display:block;width:30px;height:30px}
.brand-txt{display:flex;flex-direction:column;line-height:1.1}
.brand-txt strong{font-weight:900;letter-spacing:.04em;font-size:1.05rem}
.brand-txt span{font-family:var(--mono);font-size:.62rem;letter-spacing:.14em;text-transform:uppercase;opacity:.6}
.main-nav{display:flex;gap:22px;margin-left:auto}
.main-nav a{font-family:var(--mono);font-size:.7rem;letter-spacing:.1em;text-transform:uppercase;text-decoration:none;opacity:.85}
.main-nav a i{font-style:normal;color:var(--accent);margin-right:.4em}
.main-nav a:hover{color:var(--accent)}
.head-right{display:flex;align-items:center;gap:16px;margin-left:auto}
.main-nav + .head-right{margin-left:0}
.clock,.geo{font-size:.68rem;letter-spacing:.08em;opacity:.7;white-space:nowrap}
.geo{display:none}
@media(min-width:1100px){.geo{display:inline}}
.switcher{display:flex;border:1px solid var(--line)}
.switcher button{font-family:var(--mono);font-size:.66rem;letter-spacing:.12em;text-transform:uppercase;
  background:transparent;border:none;border-right:1px solid var(--line);color:var(--ink);
  padding:8px 12px;cursor:pointer;transition:background .3s,color .3s}
.switcher button:last-child{border-right:none}
.switcher button:hover{background:var(--line-soft)}
.switcher button.on{background:var(--accent);color:var(--on-accent)}
@media(max-width:1000px){.main-nav{display:none}}
@media(max-width:640px){.clock{display:none}.switcher button{padding:7px 8px}}
/* ---------- HERO ---------- */
.hero{position:relative;padding:clamp(48px,7vh,96px) var(--pad) 0}
.hero::before{content:"";position:absolute;inset:0;pointer-events:none;opacity:.5;
  background-image:repeating-linear-gradient(to right,var(--line-soft) 0,var(--line-soft) 1px,transparent 1px,transparent calc(100%/12));}
.hero-grid{position:relative;display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(16px,2.5vw,40px)}
.hero-left{grid-column:1/8;display:flex;flex-direction:column;gap:clamp(20px,3vh,36px)}
.kicker{font-size:.7rem;letter-spacing:.16em;text-transform:uppercase;color:var(--accent);font-weight:500}
.hero-title{font-weight:800;text-transform:uppercase;letter-spacing:-.02em;
  font-size:clamp(3rem,9.5vw,9.2rem);line-height:.92}
.hero-title .line{display:block;overflow:hidden;padding-bottom:.09em;margin-bottom:-.09em}
.hero-title .line-inner{display:inline-block;transform:translateY(112%);transition:transform 1s cubic-bezier(.19,.7,.16,1)}
body.loaded .hero-title .line:nth-child(1) .line-inner{transition-delay:.05s}
body.loaded .hero-title .line:nth-child(2) .line-inner{transition-delay:.15s}
body.loaded .hero-title .line:nth-child(3) .line-inner{transition-delay:.25s}
body.loaded .hero-title .line-inner{transform:translateY(0)}
.hero-meta{display:flex;flex-wrap:wrap;align-items:center;gap:20px;max-width:560px}
.hero-meta p{flex-basis:100%;font-size:1.02rem;opacity:.85}
.btn{display:inline-flex;align-items:center;gap:.7em;padding:.95em 1.5em;border:1px solid var(--ink);
  font-family:var(--sans);font-weight:700;font-size:.78rem;letter-spacing:.09em;text-transform:uppercase;
  text-decoration:none;color:var(--ink);background:transparent;cursor:pointer;
  transition:background .3s,color .3s,border-color .3s}
.btn span{transition:transform .3s}
.btn:hover{background:var(--ink);color:var(--bg)}
.btn:hover span{transform:translateX(5px)}
.btn.solid{background:var(--accent);border-color:var(--accent);color:var(--on-accent)}
.btn.solid:hover{background:var(--ink);border-color:var(--ink);color:var(--bg)}
.hero-right{grid-column:8/13;position:relative}
.hero-fig{position:relative;border:1px solid var(--line);background:var(--bg)}
.hero-fig img{width:100%;aspect-ratio:4/5;object-fit:cover;transition:opacity .45s ease}
.hero-fig img.dim{opacity:0}
.cross{position:absolute;font-family:var(--mono);color:var(--accent);font-size:14px;line-height:1;transform:translate(-50%,-50%)}
.c1{top:0;left:0}.c2{top:0;left:100%}.c3{top:100%;left:0}.c4{top:100%;left:100%}
.stamp{position:absolute;left:-56px;bottom:-56px;width:132px;height:132px;animation:spin 18s linear infinite}
.stamp text{fill:var(--accent);font-family:var(--mono);font-size:9.5px;letter-spacing:2.5px;text-transform:uppercase}
.stamp .stamp-core{fill:var(--accent)}
@keyframes spin{to{transform:rotate(360deg)}}
.hero-cap{margin-top:14px;font-size:.66rem;letter-spacing:.1em;text-transform:uppercase;opacity:.6}
.hero-index{position:relative;display:flex;justify-content:space-between;gap:12px;
  margin-top:clamp(32px,6vh,72px);padding:12px 0;border-top:1px solid var(--line);
  font-size:.68rem;letter-spacing:.14em;text-transform:uppercase;opacity:.75}
@media(max-width:900px){
  .hero-left{grid-column:1/13}
  .hero-right{grid-column:1/13;margin-top:8px}
  .stamp{left:auto;right:-14px;bottom:-48px;width:108px;height:108px}
}
/* ---------- MARQUEE ---------- */
.marquee{overflow:hidden;background:var(--accent);color:var(--on-accent);border-block:1px solid var(--line);transition:background .5s}
.marquee-track{display:flex;width:max-content;animation:mar 26s linear infinite}
.marquee:hover .marquee-track{animation-play-state:paused}
.mq-half{display:flex;align-items:center;white-space:nowrap;padding:14px 0;flex:0 0 auto}
.mq-half b{font-weight:800;text-transform:uppercase;letter-spacing:.06em;font-size:clamp(.95rem,2.1vw,1.4rem);padding:0 1.4rem}
.mq-half i{font-style:normal;font-size:.7em}
@keyframes mar{to{transform:translateX(-50%)}}
/* ---------- SECTIONS ---------- */
.section{padding:clamp(56px,9vh,120px) var(--pad);border-top:1px solid var(--line)}
.sec-head{display:flex;justify-content:space-between;gap:16px;font-size:.7rem;letter-spacing:.16em;
  text-transform:uppercase;margin-bottom:clamp(32px,6vh,64px)}
.sec-head span:last-child{color:var(--accent)}
.reveal{opacity:0;transform:translateY(30px);transition:opacity .8s ease,transform .8s cubic-bezier(.19,.7,.2,1)}
.reveal.in{opacity:1;transform:none}
/* manifest */
.manifest-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(16px,2.5vw,40px)}
.manifest-left{grid-column:1/4}
.big-num{font-weight:900;font-size:clamp(3rem,7vw,6.5rem);line-height:1;letter-spacing:-.03em;color:var(--accent)}
.manifest-left p{font-size:.66rem;letter-spacing:.16em;text-transform:uppercase;opacity:.6;margin-top:8px}
.manifest-right{grid-column:4/13;display:flex;flex-direction:column;gap:1.4em}
.lead{font-size:clamp(1.25rem,2.6vw,2.1rem);line-height:1.3;font-weight:500;max-width:22em}
.lead b{color:var(--accent);font-weight:700}
@media(max-width:900px){.manifest-left{grid-column:1/13}.manifest-right{grid-column:1/13}}
/* stats */
.stats-row{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid var(--line)}
.stat{padding:clamp(20px,3vw,40px);border-left:1px solid var(--line);transition:background .3s}
.stat:first-child{border-left:none}
.stat:hover{background:var(--line-soft)}
.stat-num{font-weight:800;font-size:clamp(2.2rem,5vw,4.6rem);letter-spacing:-.03em;line-height:1;display:block}
.stat-num em{font-style:normal;font-size:.45em;color:var(--accent);margin-left:.1em}
.stat-label{display:block;margin-top:10px;font-family:var(--mono);font-size:.66rem;letter-spacing:.12em;text-transform:uppercase;opacity:.65}
@media(max-width:900px){
  .stats-row{grid-template-columns:repeat(2,1fr)}
  .stat:nth-child(3){border-left:none}
  .stat:nth-child(n+3){border-top:1px solid var(--line)}
}
@media(max-width:520px){
  .stats-row{grid-template-columns:1fr}
  .stat{border-left:none}
  .stat:nth-child(n+2){border-top:1px solid var(--line)}
}
/* services */
.svc{display:grid;grid-template-columns:90px 1fr 1.3fr 60px;align-items:center;gap:16px;
  padding:clamp(18px,2.6vw,30px) 12px;border-top:1px solid var(--line);
  transition:background .35s,color .35s;cursor:default}
.svc:last-child{border-bottom:1px solid var(--line)}
.svc-n{font-family:var(--mono);font-size:.75rem;color:var(--accent)}
.svc-t{font-weight:800;text-transform:uppercase;font-size:clamp(1.1rem,2.4vw,1.8rem);letter-spacing:-.01em}
.svc-d{font-size:.9rem;opacity:.75}
.svc-a{font-size:1.3rem;justify-self:end;transition:transform .35s}
.svc:hover{background:var(--ink);color:var(--bg)}
.svc:hover .svc-a{transform:translateX(8px) rotate(-45deg)}
@media(max-width:760px){
  .svc{grid-template-columns:56px 1fr 32px}
  .svc-d{grid-column:2/3;grid-row:2;opacity:.6}
}
/* ============ 04 — WORKS ============ */
.works-grid{
  --tile-h:clamp(300px, 26vw, 460px);
  display:grid;grid-template-columns:repeat(12,1fr);
  gap:1px;background:var(--line);border:1px solid var(--line);
}
.w-tile{
  background:var(--bg);position:relative;overflow:hidden;
  display:flex;flex-direction:column;min-height:var(--tile-h);
  transition:background .5s;
}
.s7{grid-column:span 7}.s5{grid-column:span 5}.s4{grid-column:span 4}.s8{grid-column:span 8}
.w-img{flex:1 1 auto;min-height:0;overflow:hidden;background:var(--line-soft)}
.w-img img{width:100%;height:100%;object-fit:cover;transition:transform .8s cubic-bezier(.19,.7,.2,1)}
.w-tile:hover .w-img img{transform:scale(1.045)}
.w-cap{
  flex:0 0 auto;display:flex;justify-content:space-between;gap:12px;
  padding:14px 16px;border-top:1px solid var(--line);font-size:.72rem;background:var(--bg);
}
.w-title{font-weight:700;text-transform:uppercase;letter-spacing:.05em}
.w-cap .mono{font-size:.64rem;letter-spacing:.1em;text-transform:uppercase;opacity:.6;align-self:center;white-space:nowrap}
.w-type{justify-content:space-between;padding:20px;gap:16px}
.w-type .mono{font-size:.64rem;letter-spacing:.14em;text-transform:uppercase;opacity:.7}
.w-type strong{font-weight:900;text-transform:uppercase;letter-spacing:-.02em;
  font-size:clamp(2.2rem,4.6vw,4.4rem);line-height:.95;word-break:break-word}
.w-type .w-arrow{font-size:1.4rem;align-self:flex-end;margin-top:auto;transition:transform .3s}
.w-type:hover .w-arrow{transform:translateX(8px)}
.w-type.acc{background:var(--accent);color:var(--on-accent)}
.w-type.acc .mono{opacity:.85}
.w-type.ink{background:var(--ink);color:var(--bg)}
.w-index{gap:14px;padding:20px}
.w-index .mono-cap{font-size:.64rem;letter-spacing:.14em;text-transform:uppercase;opacity:.7}
.w-index ul{list-style:none;display:flex;flex-direction:column;margin-top:auto}
.w-index li{display:flex;gap:14px;align-items:baseline;padding:10px 0;border-top:1px solid var(--line);font-size:.85rem;font-weight:500}
.w-index li i{font-style:normal;font-family:var(--mono);font-size:.64rem;color:var(--accent)}
@media(max-width:900px){
  .s7,.s5,.s4,.s8{grid-column:span 12}
  .w-tile{min-height:0}
  .w-img img{height:auto;aspect-ratio:16/10}
  .w-type,.w-index{min-height:240px}
  .w-index ul{margin-top:0}
}
/* quote */
.quote{max-width:1100px}
.quote p{font-size:clamp(1.5rem,3.6vw,3rem);font-weight:600;line-height:1.18;letter-spacing:-.01em;
  border-left:8px solid var(--accent);padding-left:clamp(20px,3vw,44px)}
.quote p::before{content:"«";color:var(--accent)}
.quote p::after{content:"»";color:var(--accent)}
.quote footer{margin-top:24px;font-size:.7rem;letter-spacing:.14em;text-transform:uppercase;opacity:.65}
/* contact */
.cta-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(16px,2.5vw,40px);align-items:end}
.cta-title{grid-column:1/7;font-weight:900;text-transform:uppercase;letter-spacing:-.03em;line-height:.9;
  font-size:clamp(3rem,8vw,7.5rem)}
.cta-title .accent{color:var(--accent)}
.cta-right{grid-column:7/13;display:flex;flex-direction:column;gap:22px;align-items:flex-start}
.email{font-family:var(--mono);font-size:clamp(1rem,2.4vw,1.6rem);text-decoration:none;
  border-bottom:2px solid var(--ink);padding-bottom:4px;transition:color .3s,border-color .3s}
.email:hover{color:var(--accent);border-color:var(--accent)}
.cta-right p{opacity:.75;max-width:44ch}
@media(max-width:900px){.cta-title,.cta-right{grid-column:1/13}}
/* footer */
footer.site-foot{border-top:1px solid var(--line);padding:clamp(40px,6vh,72px) var(--pad) 0;overflow:hidden}
.foot-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;font-size:.72rem;letter-spacing:.05em;line-height:1.9}
.foot-grid h4{font-size:.64rem;letter-spacing:.16em;text-transform:uppercase;color:var(--accent);margin-bottom:10px;font-weight:500}
.foot-grid a{text-decoration:none}
.foot-grid a:hover{color:var(--accent)}
.foot-grid ul{list-style:none}
@media(max-width:760px){.foot-grid{grid-template-columns:repeat(2,1fr)}}
.wordmark{font-weight:900;text-transform:uppercase;letter-spacing:-.04em;line-height:.82;text-align:left;
  font-size:clamp(4.5rem,21vw,19rem);margin:clamp(24px,5vh,56px) 0 -0.16em;user-select:none;transition:color .5s}
.wordmark span{color:var(--accent)}
/* hint */
.hint{position:fixed;left:14px;bottom:14px;z-index:85;font-size:.62rem;letter-spacing:.12em;text-transform:uppercase;
  background:var(--ink);color:var(--bg);padding:7px 10px;opacity:.85}
@media(max-width:900px){.hint{display:none}}
/* reduced motion */
@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{animation:none!important;transition:none!important}
  .hero-title .line-inner{transform:none}
  .reveal{opacity:1;transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const SWISS_LANDING_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>

<header class="site-head">
  <div class="brand-id">
    <span class="emblem" id="emblem" aria-hidden="true"></span>
    <div class="brand-txt"><strong id="brandName">BST</strong><span id="brandFull">Bureau Swiss Type</span></div>
  </div>
  <nav class="main-nav" aria-label="Разделы">
    <a href="#manifest"><i>01</i>Манифест</a>
    <a href="#services"><i>03</i>Услуги</a>
    <a href="#works"><i>04</i>Работы</a>
    <a href="#contact"><i>06</i>Контакт</a>
  </nav>
  <div class="head-right">
    <span class="clock mono" id="clock">ZH --:--:--</span>
    <span class="geo mono" id="geo">47.37° N — 8.54° E</span>
    <div class="switcher" id="switcher" role="tablist" aria-label="Выбор бренда"></div>
  </div>
</header>

<main>
<!-- HERO -->
<section class="hero" id="top">
  <div class="hero-grid">
    <div class="hero-left">
      <p class="kicker mono" id="heroKicker"></p>
      <h1 class="hero-title" aria-live="polite">
        <span class="line"><span class="line-inner" id="hl0">&nbsp;</span></span>
        <span class="line"><span class="line-inner" id="hl1">&nbsp;</span></span>
        <span class="line"><span class="line-inner" id="hl2">&nbsp;</span></span>
      </h1>
      <div class="hero-meta">
        <p id="heroTagline"></p>
        <a class="btn" href="#works">Смотреть работы <span>→</span></a>
        <a class="btn solid" href="#contact">Начать проект <span>→</span></a>
      </div>
    </div>
    <div class="hero-right">
      <figure class="hero-fig">
        <img id="heroImg" alt="Изображение бренда" decoding="async">
        <svg class="stamp" viewBox="0 0 132 132" aria-hidden="true">
          <defs><path id="circ" d="M66,66 m-52,0 a52,52 0 1,1 104,0 a52,52 0 1,1 -104,0"/></defs>
          <text><textPath href="#circ">СЕТКА · ТИПОГРАФИКА · СИСТЕМА · ЦЮРИХ · 1957 · </textPath></text>
          <circle class="stamp-core" cx="66" cy="66" r="7"/>
        </svg>
        <span class="cross c1">+</span><span class="cross c2">+</span><span class="cross c3">+</span><span class="cross c4">+</span>
      </figure>
      <figcaption class="hero-cap mono" id="heroCap"></figcaption>
    </div>
  </div>
  <div class="hero-index mono"><span>01 / 06</span><span id="heroCity">ZÜRICH</span></div>
</section>

<!-- MARQUEE -->
<div class="marquee" aria-hidden="true"><div class="marquee-track" id="marqueeTrack"></div></div>

<!-- MANIFEST -->
<section class="section" id="manifest">
  <div class="sec-head mono"><span>01 — Манифест</span><span id="manifestIdx"></span></div>
  <div class="manifest-grid">
    <div class="manifest-left">
      <div class="big-num" id="manifestNum">1957</div>
      <p class="mono">год основания</p>
    </div>
    <div class="manifest-right">
      <p class="lead reveal" id="man1"></p>
      <p class="lead reveal" id="man2"></p>
    </div>
  </div>
</section>

<!-- STATS -->
<section class="section" id="stats">
  <div class="sec-head mono"><span>02 — Показатели</span><span>ФАКТЫ, НЕ СЛОГАНЫ</span></div>
  <div class="stats-row" id="statsRow"></div>
</section>

<!-- SERVICES -->
<section class="section" id="services">
  <div class="sec-head mono"><span>03 — Услуги</span><span>ПРОТОКОЛ РАБОТЫ</span></div>
  <div id="servicesList"></div>
</section>

<!-- WORKS -->
<section class="section" id="works">
  <div class="sec-head mono"><span>04 — Работы</span><span id="worksIdx"></span></div>
  <div class="works-grid" id="worksGrid"></div>
</section>

<!-- QUOTE -->
<section class="section" id="quote">
  <div class="sec-head mono"><span>05 — Позиция</span><span>ПРЯМАЯ РЕЧЬ</span></div>
  <blockquote class="quote reveal">
    <p id="quoteText"></p>
    <footer class="mono" id="quoteAuthor"></footer>
  </blockquote>
</section>

<!-- CONTACT -->
<section class="section" id="contact">
  <div class="sec-head mono"><span>06 — Контакт</span><span>ОТВЕТ &lt; 24 Ч</span></div>
  <div class="cta-grid">
    <h2 class="cta-title" data-t="ctaTitle">Начнём<br>проект<span class="accent">.</span></h2>
    <div class="cta-right">
      <a class="email" id="email" href="mailto:hello@bst.ch">hello@bst.ch</a>
      <p id="contactNote"></p>
      <a class="btn solid" href="mailto:hello@bst.ch" id="ctaBtn">Отправить бриф <span>→</span></a>
    </div>
  </div>
</section>
</main>

<footer class="site-foot">
  <div class="foot-grid mono">
    <div><h4>Адрес</h4><ul id="footAddr"></ul></div>
    <div><h4>Связь</h4><ul id="footContact"></ul></div>
    <div><h4>Соцсети</h4><ul><li><a href="#top">Instagram ↗</a></li><li><a href="#top">Behance ↗</a></li><li><a href="#top">Are.na ↗</a></li></ul></div>
    <div><h4>Колофон</h4><ul><li>Сетка: 12 колонок</li><li>Гарнитура: Archivo / Plex Mono</li><li>Стиль: International Typographic</li><li>© 2025, все права защищены</li></ul></div>
  </div>
  <div class="wordmark" id="wordmark">BST<span>.</span></div>
</footer>

<div class="hint mono">Клавиши 1–4 — смена бренда</div>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function swissLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= DATA ================= */
const IMG = {
  bst:{hero:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1e98062d5-3a6b-4019-ad0c-4d9a4bd0873f.png",
       w1:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/112e0e4d7-15f5-4ea8-954b-a416507df280.png",
       w2:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/13acb3abe-32ff-4ca1-a380-dfc2aff6ffd1.png"},
  nord:{hero:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/110542e6b-1a6a-4533-a13f-a3ee42030337.png",
       w1:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/162afd306-ca2e-464b-949e-54f821f7c9d6.png",
       w2:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1a2d08806-bff9-4560-95c7-772de34fdb9d.png"},
  klima:{hero:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/155465693-911c-4f33-ba05-d7a2d62385c3.png",
       w1:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/15f6f359d-98d4-40d0-a3f8-df52bb8ba3cc.png",
       w2:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/101c6a860-06a9-4361-877b-9dbf7485efc2.png"},
  orbita:{hero:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1eb30ce2a-beac-4b38-a05a-addd86a5dec6.png",
       w1:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1706ee12f-a8dd-4550-90ae-9d993610916c.png",
       w2:"https://image.qwenlm.ai/public_source/d14d24eb-3fe0-4b05-843e-79a16061294b/1066f7bc9-2c85-4b3d-b852-7a549e64c215.png"}
};
const EMBLEMS = {
  bst:'<svg viewBox="0 0 48 48"><rect width="48" height="48" fill="#E4030E"/><path d="M27 8h-6v13H8v6h13v13h6V27h13v-6H27z" fill="#fff"/></svg>',
  nord:'<svg viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" fill="#221610"/><path d="M16 33V15h4.4L32 27.4V15h4v18h-4.4L20 20.6V33z" fill="#F1E9DC"/></svg>',
  klima:'<svg viewBox="0 0 48 48"><rect width="48" height="48" fill="#10201A"/><path d="M7 38L21 13l8 14 4-6 8 17z" fill="#FF4D00"/></svg>',
  orbita:'<svg viewBox="0 0 48 48"><circle cx="24" cy="24" r="21" fill="none" stroke="#ECEEF2" stroke-width="2.5"/><ellipse cx="24" cy="24" rx="19" ry="7.5" fill="none" stroke="#3B5BFF" stroke-width="2.5" transform="rotate(-28 24 24)"/><circle cx="24" cy="24" r="3.5" fill="#3B5BFF"/></svg>'
};
const BRANDS = {
  bst:{code:"BST",name:"BST",full:"Bureau Swiss Type",city:"ZÜRICH",geo:"47.37° N — 8.54° E",
    theme:{bg:"#F7F6F2",ink:"#0E0E0E",accent:"#E4030E",on:"#FFFFFF"},
    kicker:"Бюро визуальных систем · Цюрих · осн. 1957",
    title:["Форма","следует","системе"],
    tagline:"Мы проектируем идентичности, которые работают как швейцарские механизмы: точно, долго и без единой лишней детали.",
    claim:"СЕТКА · ТИПОГРАФИКА · СИСТЕМА",
    cap:"Рис. 01 — Рабочий стол бюро. Модульная сетка, 12 колонок",
    founded:"1957", idx:"BST / ZH-1957",
    man1:"Бюро основано в Цюрихе в 1957 году. С тех пор мы держимся одной идеи: <b>дизайн — это не украшение</b>, а инструмент коммуникации. Каждое решение — от кегля до модуля сетки — подчинено задаче.",
    man2:"Мы работаем с брендами, которым нужна ясность: музеи, банки, транспортные системы, издательства. Метод неизменен — <b>исследование, прототип, система</b>. Никакой импровизации там, где должна быть конструкция.",
    stats:[[67,"","лет практики"],[214,"","проектов в архиве"],[16,"","международных наград"],[4,"","города присутствия"]],
    services:[["01","Айдентика","Знаки, логотипы и полные системы фирменного стиля с руководством на 300+ страниц."],
      ["02","Типографика и сетки","Кастомные гарнитуры, модульные сетки, плакатные и редакционные системы."],
      ["03","Диджитал-продукты","Сайты и интерфейсы, где сетка и иерархия переносятся из печати в пиксели."],
      ["04","Арт-дирекшн","Кампании, съёмки и навигационные системы для общественных пространств."]],
    works:[{k:"img",src:IMG.bst.w1,t:"Архив плаката",m:"Идентичность · 2024",s:"s7"},
      {k:"type",w:"Raster",m:"Система сеток",v:"acc",s:"s5"},
      {k:"img",src:IMG.bst.w2,t:"Печатный цех",m:"Типографика · 2023",s:"s5"},
      {k:"idx",m:"Избранные проекты 2019—2025",list:["Музей дизайна — айдентика","Kanton Bank — навигация","Helvetia Press — журнал","Tram Zürich — кампания"],s:"s7"},
      {k:"type",w:"Modul",m:"Редакционная сетка",v:"ink",s:"s4"},
      {k:"type",w:"Signet",m:"Знак и эмблема",v:"",s:"s8"}],
    quote:"Мы убираем всё, что мешает сообщению. То, что остаётся, и есть бренд.",
    author:"Бруно Хасслер — арт-директор бюро",
    email:"hello@bst.ch", note:"Бриф из двух абзацев важнее презентации на сорок слайдов. Пришлите задачу — вернёмся с планом и сметой в течение суток.",
    addr:["Limmatstrasse 21","8005 Zürich, Schweiz"], phone:"+41 44 000 19 57"},
  nord:{code:"NRD",name:"NORD",full:"NORD Kaffe Roasters",city:"OSLO",geo:"59.91° N — 10.75° E",
    theme:{bg:"#F1E9DC",ink:"#221610",accent:"#C1501F",on:"#FFF8EF"},
    kicker:"Обжарочная станция · Осло · осн. 2014",
    title:["Кофе как","точный","ритуал"],
    tagline:"Обжариваем зерно партиями по 12 килограммов и отправляем в течение 48 часов — чтобы чашка была такой, какой её задумала ферма.",
    claim:"ОБЖАРКА · СВЕЖЕСТЬ · ПРОИСХОЖДЕНИЕ",
    cap:"Рис. 01 — Партия №312, Эфиопия Иргачефф, обработка washed",
    founded:"2014", idx:"NORD / OSLO-2014",
    man1:"NORD начался с двенадцати килограммов зерна и одного вопроса: <b>почему кофе теряет вкус</b> раньше, чем доезжает до чашки? Ответ — в сроках и дисциплине обжарки.",
    man2:"Мы покупаем у двенадцати ферм напрямую, платим выше рынка и печатаем на пачке дату обжарки, а не маркетинг. <b>Прозрачность — наш рецепт.</b>",
    stats:[[12,"","ферм-партнёров"],[96," т","обжарки в год"],[48," ч","от ростера до отправки"],[91,"","средний балл SCA"]],
    services:[["01","Свежая обжарка","Партии по 12 кг, профиль под каждую ферму, отправка в течение 48 часов."],
      ["02","Подписка","Зерно каждые две недели, помол под ваш метод, пауза в один клик."],
      ["03","HoReCa и опт","Смеси под эспрессо, обучение персонала, настройка помола на месте."],
      ["04","Школа бариста","Курсы каппинга и воронки, сертификация по протоколу SCA."]],
    works:[{k:"img",src:IMG.nord.w1,t:"Кафе-партнёр",m:"HoReCa · Осло",s:"s7"},
      {k:"type",w:"Fersk",m:"Свежесть 48 часов",v:"acc",s:"s5"},
      {k:"img",src:IMG.nord.w2,t:"Подписка",m:"Упаковка · 2025",s:"s5"},
      {k:"idx",m:"Партии месяца",list:["№312 Эфиопия Иргачефф","№318 Колумбия Уила","№321 Кения Ньери","№325 Бразилия Серрадо"],s:"s7"},
      {k:"type",w:"Opphav",m:"Происхождение зерна",v:"ink",s:"s4"},
      {k:"type",w:"Brent",m:"Скандинавский профиль",v:"",s:"s8"}],
    quote:"Кофе не терпит суеты. Температура, время, помол — всё по протоколу.",
    author:"Ингрид Волле — основательница NORD",
    email:"post@nordkaffe.no", note:"Расскажите, как вы завариваете и сколько пьёте, — соберём коробку под ваш вкус и пришлём с ближайшей обжаркой.",
    addr:["Markveien 8","0554 Oslo, Norge"], phone:"+47 22 00 14 08"},
  klima:{code:"KLM",name:"KLIMA",full:"KLIMA Alpine Gear",city:"INNSBRUCK",geo:"47.26° N — 11.39° E",
    theme:{bg:"#F2F0E9",ink:"#10201A",accent:"#FF4D00",on:"#FFFFFF"},
    kicker:"Снаряжение для высокогорья · Инсбрук · осн. 1989",
    title:["Горы не","прощают","случайного"],
    tagline:"Экипировка, рассчитанная на худшую погоду и проверенная в настоящих экспедициях. Ремонтируется. Служит десятилетиями.",
    claim:"ЗАЩИТА · ВЕС · НАДЁЖНОСТЬ",
    cap:"Рис. 01 — Тестовая экспедиция №40, хребет Хоэ-Тауэрн",
    founded:"1989", idx:"KLIMA / IBK-1989",
    man1:"KLIMA появилась в гараже под Инсбруком, когда альпинисты устали выбирать между <b>тёплым и лёгким</b>. Мы шьём снаряжение, которое молчит и работает.",
    man2:"Каждая модель проходит сорок полевых тестов до запуска. Если куртка не выдержала штатного урагана — она не поступает в продажу. <b>Гарантия 25 лет</b> — не слоган, а обязательство.",
    stats:[[40,"+","экспедиций-тестов"],[25," лет","гарантии на швы"],[680," г","вес куртки Vind 3L"],[100," %","изделий ремонтопригодны"]],
    services:[["01","Хардшеллы","Мембраны 3L, проклеенные швы, крой под работу с верёвкой."],
      ["02","Термослои","Пух и синтетика по схеме слоёв для высот от 2000 до 6000 м."],
      ["03","Ремонт и гарантия","Пожизненная мастерская: молнии, швы, мембрана. Запчасти навсегда."],
      ["04","Экспедиции-тесты","Программа полевых испытаний с гидами IFMGA."]],
    works:[{k:"img",src:IMG.klima.w2,t:"Снаряжение, линия Grat",m:"Полевой тест · 2024",s:"s7"},
      {k:"type",w:"Vind",m:"Хардшелл 3L · 680 г",v:"acc",s:"s5"},
      {k:"img",src:IMG.klima.w1,t:"Куртка Vind 3L",m:"Продукт · 2025",s:"s5"},
      {k:"idx",m:"Линейки 2025",list:["Vind — хардшеллы","Snø — утепление","Grat — альпинизм","Basis — термослои"],s:"s7"},
      {k:"type",w:"Schnee",m:"Зимние серии",v:"ink",s:"s4"},
      {k:"type",w:"Grat",m:"Альпинистская линия",v:"",s:"s8"}],
    quote:"Снаряжение должно молчать и работать. Всё остальное — маркетинг.",
    author:"Маркус Эберхарт — шеф-дизайнер KLIMA",
    email:"kontakt@klima-alpine.at", note:"Опишите маршрут, сезон и высоту — соберём комплект слоёв и отправим с инструкцией по уходу.",
    addr:["Defreggerstrasse 3","6020 Innsbruck, Österreich"], phone:"+43 512 00 19 89"},
  orbita:{code:"ORB",name:"ORBITA",full:"ORBITA Watch Mfg.",city:"BIEL/BIENNE",geo:"47.14° N — 7.24° E",
    theme:{bg:"#0C0D10",ink:"#ECEEF2",accent:"#3B5BFF",on:"#FFFFFF"},
    kicker:"Мануфактура механических часов · Биль · осн. 1968",
    title:["Время,","собранное","вручную"],
    tagline:"Калибры собственной разработки, лимитированные серии и честная механика без компромиссов.",
    claim:"ТОЧНОСТЬ · МЕХАНИКА · ФОРМА",
    cap:"Рис. 01 — Калибр OR-03, ручная сборка, 88 деталей",
    founded:"1968", idx:"ORBITA / BIEL-1968",
    man1:"ORBITA — маленькая мануфактура в Биле, где один мастер собирает один калибр от первой платины до последнего винта. <b>Конвейера нет.</b> Есть верстак, лупа и время.",
    man2:"Мы выпускаем триста часов в год и не гонимся за тиражом. Каждая серия нумерована, каждый механизм регулируется в пяти позициях. <b>Механика честна</b>: либо работает, либо нет.",
    stats:[[88,"","деталей в калибре"],[41," ч","запас хода"],[5," бар","водозащита"],[300,"","часов в год — тираж"]],
    services:[["01","Мануфактурные калибры","Разработка и сборка механизмов с ручным заводом, регулировка в 5 позициях."],
      ["02","Лимитированные серии","Нумерованные тиражи от 100 до 500 экземпляров, архив каждого часа."],
      ["03","Персонализация","Гравировка, кастомные циферблаты, ремни ручной седельной прошивки."],
      ["04","Пожизненный сервис","Реставрация винтажных ORBITA и обслуживание калибров без срока давности."]],
    works:[{k:"img",src:IMG.orbita.w1,t:"Калибр OR-03",m:"Мануфактура · 2025",s:"s7"},
      {k:"type",w:"Zeit",m:"Ход и баланс",v:"acc",s:"s5"},
      {k:"img",src:IMG.orbita.w2,t:"Серия Beton",m:"Каталог · 2024",s:"s5"},
      {k:"idx",m:"Серии в производстве",list:["Klassik 38 — сталь","Beton 500 — лимит","Nachtblau 41 — синий","Handaufzug 35 — руч. завод"],s:"s7"},
      {k:"type",w:"Form",m:"Корпус и циферблат",v:"ink",s:"s4"},
      {k:"type",w:"Kaliber",m:"Собственная механика",v:"",s:"s8"}],
    quote:"Механика честна: либо работает, либо нет. Третьего не дано.",
    author:"Лука Бернаскони — мастер-часовщик",
    email:"service@orbita.ch", note:"Запишитесь на визит в мастерскую: покажем верстак, дадим подержать платину калибра и нальём кофе из турки.",
    addr:["Seevorstadt 12","2501 Biel/Bienne, Schweiz"], phone:"+41 32 000 68 00"}
};
/* ================= ENGINE ================= */
const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
const $ = id => document.getElementById(id);
const root = document.documentElement;

function scramble(el, text){
  if(el._raf) cancelAnimationFrame(el._raf);
  if(RM){ el.textContent = text; return; }
  const glyphs = "ABCDEFGHIKLMNPRSTUVXYZ0123456789#+—/";
  let frame = 0; const total = Math.max(14, Math.round(text.length * 2.2));
  const step = () => {
    frame++;
    const shown = Math.floor(text.length * (frame / total));
    let out = text.slice(0, shown);
    for(let i = shown; i < text.length; i++)
      out += text[i] === " " ? " " : glyphs[(Math.random()*glyphs.length)|0];
    el.textContent = out;
    if(frame < total) el._raf = requestAnimationFrame(step);
    else { el.textContent = text; el._raf = null; }
  };
  step();
}
function counters(scope){
  scope.querySelectorAll(".stat-num").forEach(el=>{
    const target = +el.dataset.v, suf = el.dataset.s || "";
    const paint = v => { el.innerHTML = Math.round(v) + (suf ? "<em>"+suf+"</em>" : ""); };
    if(RM){ paint(target); return; }
    const t0 = performance.now(), dur = 1100;
    const tick = t => {
      const p = Math.min(1,(t-t0)/dur), e = 1-Math.pow(1-p,3);
      paint(target*e);
      if(p<1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}
function worksHTML(b){
  return b.works.map(w=>{
    if(w.k==="img") return \`<figure class="w-tile \${w.s}"><div class="w-img"><img loading="lazy" decoding="async" src="\${w.src}" alt="\${w.t}"></div><figcaption class="w-cap"><span class="w-title">\${w.t}</span><span class="mono">\${w.m}</span></figcaption></figure>\`;
    if(w.k==="type") return \`<div class="w-tile w-type \${w.v} \${w.s}"><span class="mono">\${w.m}</span><strong>\${w.w}</strong><span class="w-arrow">→</span></div>\`;
    return \`<div class="w-tile w-index \${w.s}"><span class="mono-cap mono">\${w.m}</span><ul>\${w.list.map((li,i)=>\`<li><i>0\${i+1}</i><span>\${li}</span></li>\`).join("")}</ul></div>\`;
  }).join("");
}
function setBrand(id, first){
  const b = BRANDS[id]; if(!b) return;
  root.style.setProperty("--bg", b.theme.bg);
  root.style.setProperty("--ink", b.theme.ink);
  root.style.setProperty("--accent", b.theme.accent);
  root.style.setProperty("--on-accent", b.theme.on);
  $("emblem").innerHTML = EMBLEMS[id];
  scramble($("brandName"), b.name);
  $("brandFull").textContent = b.full;
  $("heroKicker").textContent = b.kicker;
  scramble($("hl0"), b.title[0]);
  scramble($("hl1"), b.title[1]);
  scramble($("hl2"), b.title[2] + ".");
  $("heroTagline").textContent = b.tagline;
  $("heroCap").textContent = b.cap;
  $("heroCity").textContent = b.city;
  $("geo").textContent = b.geo;
  $("manifestIdx").textContent = b.idx;
  $("manifestNum").textContent = b.founded;
  $("man1").innerHTML = b.man1;
  $("man2").innerHTML = b.man2;
  $("worksIdx").textContent = "АРХИВ / " + b.city;
  $("quoteText").textContent = b.quote;
  $("quoteAuthor").textContent = "— " + b.author;
  $("email").textContent = b.email;
  $("email").href = "mailto:" + b.email;
  $("ctaBtn").href = "mailto:" + b.email;
  $("contactNote").textContent = b.note;
  $("footAddr").innerHTML = b.addr.map(a=>"<li>"+a+"</li>").join("");
  $("footContact").innerHTML = \`<li><a href="mailto:\${b.email}">\${b.email}</a></li><li>\${b.phone}</li>\`;
  $("wordmark").innerHTML = b.name + "<span>.</span>";
  /* hero image swap with fade */
  const img = $("heroImg");
  img.classList.add("dim");
  const pre = new Image();
  const show = () => { img.src = IMG[id].hero; img.alt = b.cap; img.classList.remove("dim"); };
  pre.onload = show;
  pre.onerror = show;
  pre.src = IMG[id].hero;
  if(pre.complete && pre.naturalWidth > 0) show();
  /* marquee: two identical halves for a seamless -50% loop */
  const items = Array(4).fill(\`<b>\${b.claim}</b><i>●</i><b>\${b.name} — \${b.city}</b><i>●</i>\`).join("");
  $("marqueeTrack").innerHTML = \`<div class="mq-half">\${items}</div><div class="mq-half" aria-hidden="true">\${items}</div>\`;
  /* stats */
  $("statsRow").innerHTML = b.stats.map(s=>\`<div class="stat"><span class="stat-num" data-v="\${s[0]}" data-s="\${s[1]}">0</span><span class="stat-label">\${s[2]}</span></div>\`).join("");
  counters($("statsRow"));
  /* services */
  $("servicesList").innerHTML = b.services.map(s=>\`<div class="svc"><span class="svc-n">\${s[0]}</span><span class="svc-t">\${s[1]}</span><span class="svc-d">\${s[2]}</span><span class="svc-a">→</span></div>\`).join("");
  /* works */
  $("worksGrid").innerHTML = worksHTML(b);
  /* switcher state */
  document.querySelectorAll(".switcher button").forEach(btn=>btn.classList.toggle("on", btn.dataset.brand===id));
  document.title = \`\${b.name} — \${b.full}\`;
  try{ history.replaceState(null, "", "#"+id); }catch(e){}
  if(!first) observeReveals();
}
/* reveal observer */
const io = new IntersectionObserver(es=>es.forEach(e=>{
  if(e.isIntersecting){ e.target.classList.add("in"); io.unobserve(e.target); }
}),{threshold:.15});
function observeReveals(){ document.querySelectorAll(".reveal:not(.in)").forEach(el=>io.observe(el)); }
/* switcher build */
const sw = $("switcher");
Object.keys(BRANDS).forEach(k=>{
  const btn = document.createElement("button");
  btn.type = "button";
  btn.dataset.brand = k;
  btn.textContent = BRANDS[k].code;
  btn.setAttribute("role","tab");
  btn.setAttribute("aria-label", "Бренд: " + BRANDS[k].name);
  btn.addEventListener("click", ()=> setBrand(k));
  sw.appendChild(btn);
});
addEventListener("keydown", e=>{
  if(e.metaKey || e.ctrlKey || e.altKey) return;
  const map = {"1":"bst","2":"nord","3":"klima","4":"orbita"};
  if(map[e.key]) setBrand(map[e.key]);
});
/* clock (Zürich) */
const fmt = new Intl.DateTimeFormat("ru-RU",{timeZone:"Europe/Zurich",hour:"2-digit",minute:"2-digit",second:"2-digit"});
function tickClock(){ $("clock").textContent = "ZH " + fmt.format(new Date()); }
tickClock();
setInterval(tickClock, 1000);
/* init */
const hash = (location.hash||"").replace("#","");
setBrand(BRANDS[hash] ? hash : '${brand}', true);
observeReveals();
requestAnimationFrame(()=>requestAnimationFrame(()=>document.body.classList.add("loaded")));
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.reveal,.rv,[data-rv]').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('.line-inner').forEach(function(el){el.style.transform='translateY(0)';});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getSwissLandingPageHtml(data: SwissLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'bst'
  return `<!DOCTYPE html>
<html lang="ru" data-brand="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo/900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/500.css">
<style>${SWISS_LANDING_CSS}</style>
</head>
<body>
${SWISS_LANDING_HTML_BODY}
<script>${swissLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
